import React, { useEffect, useState } from "react";

import { isEmpty } from "lodash";

import { API, graphqlOperation } from "aws-amplify";
import { locationBySortKey } from "../../../graphql/queries";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField as MuiTextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  ArrayField,
  Button,
  Datagrid,
  FileInput,
  TextField,
  useNotify,
} from "react-admin";

import {
  AddAllIcon,
  AttachFileIcon,
  CancelIcon,
  CloseIcon,
} from "../../../components/common/iconography";
import { FileField } from "../../../components/common/FileField";
import { getCustomFieldId } from "../../../components/common/CustomFields";

import { MAX_FILE_SIZE, MAX_FILE_SIZE_MSG } from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import { csv2arr } from "../../../utils/fileUtils";
import { getLocationFullName } from "../../../utils/locationUtils";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    justifyContent: "space-between",
  },
  title: {
    paddingTop: theme.spacing(2),
  },
  dialogContent: {
    // https://stackoverflow.com/questions/47698037/how-can-i-set-a-height-to-a-dialog-in-material-ui
    height: "90vh",
  },
  scrollableGrid: {
    overflowX: "auto",
    overflowY: "hidden",
    width: "100%",
    border: "1px solid #ccc",
    borderRadius: 4,
    paddingBottom: 8,
    "&::-webkit-scrollbar": {
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  },
}));

const fetchLocationsMap = async () => {
  // listLocations (no index) starts at the table scan origin; AppSync auth
  // filters out rows from other yards, so the first page often comes back
  // empty with just a nextToken. Use the reloadYardId-indexed query and
  // page through to be safe.
  const reloadYardId = window.appUser?.reloadYardId;
  const rawItems = [];
  let nextToken = null;
  do {
    const response = await API.graphql(
      graphqlOperation(locationBySortKey, {
        reloadYardId,
        limit: 1000,
        nextToken,
      }),
    );
    const page = response?.data?.locationBySortKey;
    if (page?.items?.length) rawItems.push(...page.items.filter(Boolean));
    nextToken = page?.nextToken || null;
  } while (nextToken);

  const map = rawItems.reduce((acc, location) => {
    const locationFullName = getLocationFullName(location);
    if (locationFullName) acc[locationFullName] = location;
    return acc;
  }, {});
  return map;
};

const validateCSV = (form, csvArray, fieldMap, isStockImport) => {
  const formState = form.getState();
  const errors = [];

  const poColumn = fieldMap["poNumber"];
  const productCodeColumn = fieldMap["productCode"];
  const productNameColumn = fieldMap["productName"];
  const quantityColumn = fieldMap["quantityAdvised"];
  const weightColumn = fieldMap["weight"];
  const pcsColumn = fieldMap["pcs"];

  let missingCodeName = !productNameColumn && !productCodeColumn;
  let invalidCodeName = false;
  let invalidQty = false;
  let zeroQty = false;
  let invalidWeight = false;
  let invalidPcs = false;
  let needsPoMatchCheck =
    !isStockImport && poColumn && formState.values.poNumber;

  csvArray.forEach((row) => {
    if (needsPoMatchCheck) {
      const poNumber = row[poColumn]?.trim();
      if (poNumber === formState.values.poNumber) needsPoMatchCheck = false;
    }

    const productCode = row[productCodeColumn]?.trim();
    const productName = row[productNameColumn]?.trim();
    if (!missingCodeName && !productName && !productCode)
      invalidCodeName = true;

    if (quantityColumn) {
      const quantity = Number(row[quantityColumn]); // null = 0, undefined = NaN
      if (isNaN(quantity) || quantity < 0) invalidQty = true; // null = false, undefined = true
      // A stock import sets on-hand quantities, so a zero (or blank) row has
      // nothing to import.
      if (isStockImport && quantity === 0) zeroQty = true;
    }

    if (weightColumn) {
      const weight = Number(row[weightColumn]); // null = 0, undefined = NaN
      if (isNaN(weight) || weight < 0) invalidWeight = true; // null = false, undefined = true
    }

    if (pcsColumn && row[pcsColumn]?.trim() !== "") {
      const pcs = Number(row[pcsColumn]);
      if (isNaN(pcs) || pcs <= 0) invalidPcs = true;
    }
  });

  if (missingCodeName)
    errors.push(
      `You must select at least one of "Product Code" or "Product Name".`,
    );
  if (invalidCodeName)
    errors.push(`Each row must contain "Product Code" or "Product Name".`);
  if (needsPoMatchCheck)
    errors.push(`No rows match PO Number: ${formState.values.poNumber}.`);
  if (invalidQty)
    errors.push(`Quantity must be numeric and greater than 0, or be blank.`);
  if (zeroQty)
    errors.push(`Each row must contain a "Quantity On Hand" greater than 0.`);
  if (invalidWeight)
    errors.push(`Weight must be numeric and greater than 0, or be blank.`);
  if (invalidPcs)
    errors.push(
      `Pieces/Unit (PPU) must be numeric and greater than 0 if specified.`,
    );

  return errors;
};

const processCSV = async (
  str,
  delim = ",",
  setLoading,
  setColumns,
  setCSVData,
  setCSVErrors,
  notify,
) => {
  let errors = [];
  let lengthError = false;

  try {
    let headers = str
      .slice(0, str.indexOf("\n"))
      .replace("\r", "")
      .split(delim);
    headers = headers.filter((head) => head !== "");
    const hlen = headers.length;

    let rows = csv2arr(str.slice(str.indexOf("\n") + 1));
    rows = rows.filter((row) => row.join("") !== "");

    let newArray = rows
      .map((row) => {
        if (!isEmpty(row) && row.length > 1) {
          const eachObject = headers.reduce((obj, header, i) => {
            obj[header] = row[i];
            return obj;
          }, {});

          if (row.length > hlen) {
            eachObject["error"] = true;
            lengthError = true;
          }

          return eachObject;
        }
      })
      .filter((itm) => itm);

    if (lengthError)
      errors.push(
        `Values in extra columns, or extra unquoted commas within entries.`,
      );

    if (errors.length > 0) {
      setCSVErrors(errors);
    } else {
      setCSVData(newArray);

      const columns = headers.map((itm) => {
        return { id: itm, name: itm };
      });
      setColumns([{ id: null, name: <span>&nbsp;</span> }, ...columns]);
    }
  } catch (err) {
    logError("inboundShipments:ManifestUpload:processCSV", err, notify);
  } finally {
    setLoading(false);
  }
};

export const ManifestUploadDialog = ({
  form,
  account,
  isOpen,
  setIsOpen,
  handleAddUpload,
  ...props
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [csvData, setCSVData] = useState([]);
  const [csvErrors, setCSVErrors] = useState([]);
  const [fieldMap, setFieldMap] = useState({});
  const stockImportField = form.getFieldState("stockImport");
  const isStockImport = Boolean(stockImportField?.value === true);
  const [locationMap, setLocationMap] = useState({});

  useEffect(() => {
    if (isStockImport) {
      fetchLocationsMap().then((locations) => {
        setLocationMap(locations);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStockImport]);

  const handleAdd = () => {
    if (csvData?.length > 0) {
      const errors = validateCSV(form, csvData, fieldMap, isStockImport);
      if (errors?.length > 0) {
        setCSVErrors(errors);
      } else {
        handleAddUpload(csvData, fieldMap, isStockImport, locationMap);
        handleClose();
      }
    } else handleClose();
  };

  const handleClose = () => {
    form.change("manifestFile", undefined);
    setFieldMap({});
    setCSVData([]);
    setCSVErrors([]);
    setIsOpen(false);
  };

  return (
    <Dialog maxWidth="xl" open={isOpen} fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography className={classes.title}>
          {isStockImport ? "Stock Import" : "Manifest Upload"}
        </Typography>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <ManifestUpload
          {...props}
          form={form}
          account={account}
          isStockImport={isStockImport}
          setLoading={setLoading}
          csvData={csvData}
          setCSVData={setCSVData}
          csvErrors={csvErrors}
          setCSVErrors={setCSVErrors}
          fieldMap={fieldMap}
          setFieldMap={setFieldMap}
          classes={classes}
        />
      </DialogContent>
      <DialogActions>
        <Button label="Cancel" color="default" onClick={handleClose}>
          <CancelIcon />
        </Button>
        <Button
          label="Add Records"
          color="primary"
          disabled={loading || csvData?.length === 0}
          onClick={handleAdd}
        >
          <AddAllIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ManifestUpload = ({
  form,
  account,
  isStockImport = false,
  setLoading,
  csvData,
  setCSVData,
  csvErrors,
  setCSVErrors,
  fieldMap,
  setFieldMap,
  classes,
  ...props
}) => {
  const notify = useNotify();
  const [columns, setColumns] = useState([]);
  const fields = [
    { id: "poNumber", name: "PO Number" },
    { id: "productCode", name: "Product Code" },
    { id: "productName", name: "Product Name" },
    {
      id: "quantityAdvised",
      name: isStockImport ? "Quantity On Hand" : "Quantity Advised",
    },
    { id: "pcs", name: "Pieces/Unit" },
  ];
  if (account.defaultWeightUOM) {
    fields.push({ id: "weight", name: `${account.defaultWeightUOM}/Unit` });
  } else {
    fields.push({ id: "weight", name: "Weight/Unit" });
  }
  if (isStockImport) {
    // Optional CSV columns for legacy data
    fields.push({ id: "railCarNumber", name: "Car/Trl Number" });
    fields.push({ id: "lotNumber", name: "Lot Number" });
    fields.push({ id: "location", name: "Location" });
  }
  if (account.inventoryCustomFields?.length > 0) {
    account.inventoryCustomFields.forEach((itm) =>
      fields.push({ id: getCustomFieldId(itm.name), name: itm.name }),
    );
  }

  const handleCSVFile = async (value) => {
    try {
      setLoading(true);
      setCSVErrors([]);
      if (!value) {
        form.change("manifestFile", undefined);
        setCSVData([]);
      } else if (value?.size > MAX_FILE_SIZE) {
        notify(MAX_FILE_SIZE_MSG, "warning");
        form.change("manifestFile", undefined);
        setCSVData([]);
      } else {
        const fileState = form.getFieldState("manifestFile");
        const reader = new FileReader();
        reader.onload = function (e) {
          const text = e.target.result;
          processCSV(
            text,
            ",",
            setLoading,
            setColumns,
            setCSVData,
            setCSVErrors,
            notify,
          );
        };
        let blob = await fetch(fileState.value.src).then((r) => r.blob());
        reader.readAsText(blob);
      }
    } catch (error) {
      logError("inboundShipments:ManifestUpload:handleCSVFile", error, notify);
    }
  };

  const handleColumnChange = (value, field) => {
    fieldMap[field.id] = value;
    setFieldMap({ ...fieldMap });
  };

  return (
    <Grid container direction="column" spacing={1}>
      <Grid container direction="row" spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="overline" color="textSecondary" gutterBottom>
            Instructions
          </Typography>
          <ol>
            <li>
              {" "}
              Prepare a Manifest spreadsheet with columns and values for all
              required Tally Sheet fields.{" "}
            </li>
            <li>
              {" "}
              If Manifest includes PO Number entries, only rows with a PO Number
              matching that of the Inbound Shipment will be imported.{" "}
            </li>
            <li>
              {" "}
              Save as a comma-separated-values (.CSV) file and upload here{" "}
            </li>
            <li>
              {" "}
              If errors are found, review, correct the errors and re-upload{" "}
            </li>
            <li> For help, contact us at 877-TRANSLOAD / 877-872-6756 </li>
          </ol>
        </Grid>
        <Grid item xs={12} md={6}>
          <FileInput
            source="manifestFile"
            label="MANIFEST UPLOAD"
            variant="outlined"
            fullWidth
            placeholder={
              <p>
                <AttachFileIcon fontSize="large" /> <br /> Drop a CSV File to
                upload, or click to select it.
              </p>
            }
            accept=".csv"
            onChange={handleCSVFile}
            helperText={false}
          >
            <FileField source="src" title="title" target="_blank" />
          </FileInput>
        </Grid>
      </Grid>
      {csvErrors?.length > 0 && (
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12}>
            <Typography variant="overline" color="error">
              {" "}
              Loading Errors:{" "}
            </Typography>
            <ul>
              {csvErrors.map((error) => (
                <Typography color="error">
                  {" "}
                  <li>{error}</li>{" "}
                </Typography>
              ))}
            </ul>
          </Grid>
        </Grid>
      )}

      {columns.length > 0 && (
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary" gutterBottom>
              {" "}
              Match input fields to columns{" "}
            </Typography>
          </Grid>
          {fields.map((field) => (
            <Grid item xs={12} sm={6} md={2}>
              <MuiTextField
                select
                id={`select_${field.id}`}
                size="small"
                margin="dense"
                variant="outlined"
                label={field.name}
                fullWidth
                value={fieldMap[field.id]}
                onChange={(e) => handleColumnChange(e.target.value, field)}
              >
                {columns.map((col) => (
                  <MenuItem key={field.id + col.id} value={col.id}>
                    {col.name}
                  </MenuItem>
                ))}
              </MuiTextField>
            </Grid>
          ))}
          {csvData?.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="default" gutterBottom>
                {" "}
                {`Found 0 records.`}
              </Typography>
            </Grid>
          )}
          {csvData?.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography
                  variant="overline"
                  color="textSecondary"
                  gutterBottom
                >
                  {`Preview (Showing 3 of ${csvData.length} rows)`}
                </Typography>
              </Grid>

              {/* Preview table */}
              <Grid item xs={12} className={classes.scrollableGrid}>
                <ArrayField
                  source="preview"
                  record={{ preview: csvData.slice(0, 3) }} // 3 rows only
                >
                  <Datagrid
                    rowClick={false}
                    bulkActionButtons={false}
                    expand={null}
                  >
                    {/* Iterate the header definitions once */}
                    {columns
                      .filter((col) => col.id) // skip any placeholder col.id===null
                      .map((col) => (
                        <TextField
                          key={col.id}
                          source={col.id}
                          label={col.name}
                        />
                      ))}
                  </Datagrid>
                </ArrayField>
              </Grid>
            </>
          )}
        </Grid>
      )}
    </Grid>
  );
};
